import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import playerProfileService, { PlayerProfile } from '../services/playerProfileService';

/**
 * Globaler Spieler-Verlinker: löst Spieler-NAMEN (aus Ranglisten, Pro-Spielen,
 * Historien …) gegen die redaktionellen PlayerProfiles auf und macht sie
 * überall klickbar (→ /spieler/:slug). Profile werden EINMAL pro Session
 * geladen und als normalisierte Namens-Map gecacht — kein Request pro Name.
 */

let cachePromise: Promise<Map<string, PlayerProfile>> | null = null;

/** Namens-Normalisierung: lowercase, Akzente raus, Whitespace kollabiert */
const normalizeName = (name: string): string =>
  name
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/\s+/g, ' ');

const loadProfileMap = (): Promise<Map<string, PlayerProfile>> => {
  if (!cachePromise) {
    cachePromise = playerProfileService
      .list({ limit: 1000 })
      .then((profiles) => {
        const map = new Map<string, PlayerProfile>();
        for (const profile of profiles ?? []) {
          if (!profile?.slug) continue;
          const keys = new Set<string>();
          if (profile.name) keys.add(normalizeName(profile.name));
          const composed = [profile.firstname, profile.lastname].filter(Boolean).join(' ');
          if (composed) keys.add(normalizeName(composed));
          keys.forEach((key) => {
            if (key && !map.has(key)) map.set(key, profile);
          });
        }
        return map;
      })
      .catch(() => {
        // Fehlgeschlagenen Load nicht dauerhaft cachen
        cachePromise = null;
        return new Map<string, PlayerProfile>();
      });
  }
  return cachePromise;
};

/** Profil per Name auflösen (null, wenn kein redaktionelles Profil existiert) */
export async function findPlayerProfileByName(name?: string | null): Promise<PlayerProfile | null> {
  if (!name || !name.trim()) return null;
  const map = await loadProfileMap();
  return map.get(normalizeName(name)) ?? null;
}

/** React-Hook: Profil zu einem Spielernamen (null solange lädt/kein Treffer) */
export function usePlayerProfileByName(name?: string | null): PlayerProfile | null {
  const [profile, setProfile] = useState<PlayerProfile | null>(null);
  useEffect(() => {
    let cancelled = false;
    setProfile(null);
    findPlayerProfileByName(name).then((result) => {
      if (!cancelled) setProfile(result);
    });
    return () => {
      cancelled = true;
    };
  }, [name]);
  return profile;
}

interface PlayerLinkProps {
  name?: string | null;
  className?: string;
  /** Klassen, die NUR bei vorhandenem Profil zusätzlich gelten (Hover etc.) */
  linkClassName?: string;
  children?: React.ReactNode;
  /** Klick nicht an Eltern-Links/-Karten durchreichen (Default true) */
  stopPropagation?: boolean;
}

/**
 * Rendert den Spielernamen (oder children). Existiert ein PlayerProfile,
 * wird der Name klickbar (→ /spieler/:slug) — bewusst per navigate statt
 * <Link>, damit die Komponente auch INNERHALB verlinkter Karten funktioniert
 * (kein verschachteltes <a>).
 */
const PlayerLink: React.FC<PlayerLinkProps> = ({
  name,
  className,
  linkClassName,
  children,
  stopPropagation = true,
}) => {
  const navigate = useNavigate();
  const profile = usePlayerProfileByName(name);
  const content = children ?? name ?? '';

  if (!profile?.slug) {
    return <span className={className}>{content}</span>;
  }

  return (
    <span
      role="link"
      tabIndex={0}
      onClick={(event) => {
        if (stopPropagation) {
          event.stopPropagation();
          event.preventDefault();
        }
        navigate(`/spieler/${profile.slug}`);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          event.stopPropagation();
          navigate(`/spieler/${profile.slug}`);
        }
      }}
      className={`cursor-pointer transition-colors hover:text-[#3fced1] hover:underline underline-offset-2 ${className ?? ''} ${linkClassName ?? ''}`}
    >
      {content}
    </span>
  );
};

export default PlayerLink;
